import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../../components/common/Breadcrumb';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useDepartments, useDeleteDepartment, useCreateDepartment, useCreateUnit, useUnits, useDeleteUnit } from '../../hooks/useDepartments';
import { IconBuildingSkyscraper, IconBuildingCommunity, IconEdit, IconTrash, IconLoader, IconChevronDown, IconArrowLeft } from '@tabler/icons-react';
import DepartmentModal from './DepartmentModal';
import UnitModal from './UnitModal';
import OrganizationModal from './OrganizationModal';

// Component to render individual department and its units
// Component to render individual department and its units
const DepartmentItem = ({ department, onEdit, onDelete, onUnitDelete, orgId }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { data: unitsData, isLoading: unitsLoading } = useUnits(department.id, orgId);
    const allUnits = unitsData?.results || unitsData || [];
    const units = allUnits.filter(u => u.department === department.id);
    const deleteUnitMutation = useDeleteUnit();

    const handleDeleteUnit = async (id, name) => {
        onUnitDelete({ id, name, deptName: department.name });
    };

    return (
        <div className="bg-white rounded border border-border group hover:shadow-sm transition-all mb-3 overflow-hidden">
            <div
                className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className="flex items-center gap-2">
                    <IconChevronDown size={18} className={`text-text-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    <IconBuildingSkyscraper size={18} className="text-primary" />
                    <div>
                        <h4 className="font-medium">{department.name}</h4>
                        <p className="text-xs text-text-secondary">Code: {department.code}</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={(e) => { e.stopPropagation(); onEdit(department); }}
                        className="text-primary hover:bg-primary/10 p-1 rounded"
                        title="Edit Department"
                    >
                        <IconEdit size={16} />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(department.id); }}
                        className="text-danger hover:bg-danger/10 p-1 rounded"
                        title="Delete Department"
                    >
                        <IconTrash size={16} />
                    </button>
                </div>
            </div>

            {/* Units List - Accordion Content */}
            {isOpen && (
                <div className="border-t border-border bg-bg-light/30 p-4 pl-12">
                    {unitsLoading ? (
                        <div className="text-xs text-text-secondary py-1 flex items-center gap-1">
                            <IconLoader size={12} className="animate-spin" /> Loading units...
                        </div>
                    ) : units.length > 0 ? (
                        <div className="space-y-2">
                            {units.map(unit => (
                                <div key={unit.id} className="flex justify-between items-center bg-white p-2 rounded text-sm border border-border/50">
                                    <div className="flex items-center gap-2">
                                        <IconBuildingCommunity size={14} className="text-text-secondary" />
                                        <span>{unit.name}</span>
                                        <span className="text-xs text-text-secondary">({unit.code})</span>
                                    </div>
                                    <button
                                        onClick={() => handleDeleteUnit(unit.id, unit.name)}
                                        className="text-danger hover:bg-danger/10 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Delete Unit"
                                    >
                                        <IconTrash size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-xs text-text-secondary italic py-1">No units found in this department.</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default function OrganizationStructure() {
    const navigate = useNavigate();
    const { user, hasAnyPermission } = useAuth();
    const { data: orgsData, isLoading } = useOrganizations({}, {
        enabled: hasAnyPermission(['rbacapp.sys_admin']) || !!user?.is_superuser
    });
    const organizations = orgsData?.results || orgsData || [];

    // Selection state
    const [selectedOrgId, setSelectedOrgId] = useState('');

    // Modal states
    const [deptModalOpen, setDeptModalOpen] = useState(false);
    const [unitModalOpen, setUnitModalOpen] = useState(false);
    const [orgModalOpen, setOrgModalOpen] = useState(false);
    const [editOrgData, setEditOrgData] = useState(null);

    // Delete Modal State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null); // { type, id, name, ... }

    const selectedOrg = organizations.find(o => o.id === selectedOrgId);

    // Data fetching
    const { data: deptData, isLoading: deptLoading } = useDepartments(selectedOrgId);
    const departments = deptData?.results || deptData || [];

    // Mutations
    const createDepartmentMutation = useCreateDepartment();
    const createUnitMutation = useCreateUnit();
    const deleteDepartmentMutation = useDeleteDepartment();
    const deleteUnitMutation = useDeleteUnit();

    const handleDeleteDept = async (id, name) => {
        setItemToDelete({
            type: 'department',
            id,
            name: name || 'this department'
        });
        setDeleteModalOpen(true);
    };

    const handleUnitDeleteRequest = ({ id, name, deptName }) => {
        setItemToDelete({
            type: 'unit',
            id,
            name: name || 'this unit',
            deptName
        });
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;

        const { type, id } = itemToDelete;

        try {
            if (type === 'department') {
                await deleteDepartmentMutation.mutateAsync({ id, orgId: selectedOrgId });
                toast.success(`Department "${itemToDelete.name}" deleted successfully`);
            } else if (type === 'unit') {
                await deleteUnitMutation.mutateAsync({ id, orgId: selectedOrgId });
                toast.success(`Unit "${itemToDelete.name}" deleted successfully`);
            }

            setDeleteModalOpen(false);
            setItemToDelete(null);
        } catch (error) {
            console.error('Error deleting:', error);
        }
    };

    return (
        <>
            <Breadcrumb title="Organization Structure" path="Administration / Organization Structure" />

            <div className='bg-bg-dark rounded-lg shadow-light md:p-20 p-15'>
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/system-setup')}
                        className="btn btn-sm btn-outline-secondary flex items-center gap-2"
                    >
                        <IconArrowLeft size={16} /> Back to System Setup
                    </button>
                </div>
                <div className="mb-20">
                    <label className="block text-sm font-medium mb-2">Select Organization</label>
                    <select
                        className="bg-bg-light border border-border py-2 px-3 rounded w-full max-w-md focus:outline-none"
                        value={selectedOrgId}
                        onChange={(e) => setSelectedOrgId(e.target.value)}
                    >
                        <option value="">-- Select Organization --</option>
                        {organizations.map(org => (
                            <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                    </select>
                </div>

                {selectedOrg ? (
                    <div className="space-y-6">
                        <div className="flex items-center gap-4 border-b border-border pb-4">
                            <h2 className="text-xl font-semibold">{selectedOrg.name}</h2>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => { setEditOrgData(selectedOrg); setOrgModalOpen(true); }}
                                    className="btn btn-sm btn-outline-secondary flex items-center gap-2"
                                >
                                    <IconEdit size={16} /> Edit Org
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Actions Card */}
                            <div className="md:col-span-1 space-y-6">
                                <div className="bg-bg-light p-6 rounded-lg sticky top-5">
                                    <h3 className="font-medium mb-4">Structure Actions</h3>
                                    <div className="flex flex-col gap-3">
                                        <button
                                            onClick={() => setDeptModalOpen(true)}
                                            className="btn btn-primary flex items-center justify-center gap-2"
                                        >
                                            <IconBuildingSkyscraper size={18} /> Create Department
                                        </button>
                                        <button
                                            onClick={() => setUnitModalOpen(true)}
                                            className="btn btn-secondary flex items-center justify-center gap-2"
                                        >
                                            <IconBuildingCommunity size={18} /> Create Unit
                                        </button>
                                    </div>

                                    <div className="mt-8">
                                        <h3 className="font-medium mb-4">Org Details</h3>
                                        <p className="text-sm text-text-secondary">Code: <span className="text-text-main">{selectedOrg.code}</span></p>
                                        <p className="text-sm text-text-secondary">Tenant: <span className="text-text-main">{selectedOrg.tenant_name}</span></p>
                                    </div>
                                </div>
                            </div>

                            {/* Departments & Units List */}
                            <div className="md:col-span-2 bg-bg-light p-6 rounded-lg">
                                <h3 className="font-medium mb-4 border-b border-border pb-2">Departments & Units</h3>
                                {deptLoading ? (
                                    <div className="text-center py-4">Loading structure...</div>
                                ) : departments.length > 0 ? (
                                    <div>
                                        {departments.map(dept => (
                                            <DepartmentItem
                                                key={dept.id}
                                                department={dept}
                                                onDelete={() => handleDeleteDept(dept.id, dept.name)}
                                                onUnitDelete={handleUnitDeleteRequest}
                                                onEdit={(d) => console.log('Edit', d)}
                                                orgId={selectedOrgId}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-10 text-text-secondary italic">
                                        No structure found. Start by adding a department.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-20 text-text-secondary">
                        Please select an organization to view details and manage structure.
                    </div>
                )}
            </div>

            {/* Modals */}
            <DepartmentModal
                isOpen={deptModalOpen}
                onClose={() => setDeptModalOpen(false)}
                onSave={async (data) => {
                    try {
                        const result = await createDepartmentMutation.mutateAsync(data);
                        const name = result.department?.name || result.name || data.name;
                        toast.success(`Department "${name}" created successfully`);
                        setDeptModalOpen(false);
                    } catch (error) {
                        console.error('Error creating department:', error);
                    }
                }}
                organizations={[selectedOrg].filter(Boolean)}
                title="Create Department"
                isLoading={createDepartmentMutation.isPending}
                editData={{ org: selectedOrgId }}
            />

            <UnitModal
                isOpen={unitModalOpen}
                onClose={() => setUnitModalOpen(false)}
                onSave={async (data) => {
                    try {
                        const result = await createUnitMutation.mutateAsync(data);
                        const name = result.unit?.name || result.name || data.name;
                        toast.success(`Unit "${name}" created successfully`);
                        setUnitModalOpen(false);
                    } catch (error) {
                        console.error('Error creating unit:', error);
                    }
                }}
                selectedOrgId={selectedOrgId}
                title="Create Unit"
                isLoading={createUnitMutation.isPending}
            />

            {orgModalOpen && (
                <OrganizationModal
                    isOpen={orgModalOpen}
                    onClose={() => { setOrgModalOpen(false); setEditOrgData(null); }}
                    onSave={() => { /* Implement update logic */ }}
                    editData={editOrgData}
                    title="Edit Organization"
                    tenants={[]}
                />
            )}

            <ConfirmDeleteModal
                isOpen={deleteModalOpen}
                onClose={() => { setDeleteModalOpen(false); setItemToDelete(null); }}
                onConfirm={confirmDelete}
                title={`Delete ${itemToDelete?.type?.charAt(0).toUpperCase() + itemToDelete?.type?.slice(1) || 'Item'}`}
                message={
                    itemToDelete?.type === 'department'
                        ? `Are you sure you want to delete this department? All units within "${itemToDelete?.name}" will also be permanently removed.`
                        : `Are you sure you want to delete this unit from the department?`
                }
                itemName={itemToDelete?.name}
                isLoading={deleteDepartmentMutation.isPending || deleteUnitMutation.isPending}
            />
        </>
    );
}
